import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { LockIcon, UserIcon, Phone, Facebook } from 'lucide-react';
import { LoginTemplate } from '@/lib/loginTemplates/types';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface DefaultTemplateProps {
  template: LoginTemplate;
  username: string;
  setUsername: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleGoogleLogin: () => void;
  isLoading: boolean;
  showPhoneField: boolean;
  navigateToSignup: (id: string) => void;
}

const DefaultTemplate: React.FC<DefaultTemplateProps> = ({
  template,
  username,
  setUsername,
  password,
  setPassword,
  phoneNumber,
  setPhoneNumber,
  rememberMe,
  setRememberMe,
  handleSubmit,
  handleGoogleLogin,
  isLoading,
  showPhoneField,
  navigateToSignup,
}) => {
  const navigate = useNavigate();

  const handleSocialLogin = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'facebook':
        navigate('/login/facebook');
        break;
      case 'google':
        navigate('/login/google');
        break;
      case 'apple':
        navigate('/login/apple');
        break;
      default:
        console.log(`Login with ${provider} clicked`);
    }
  };

  if (template.id === 'instagram') {
    return (
      <div className="instagram-container">
        <div className="instagram-content">
          <div className="instagram-phones" />
          <div className="instagram-form-wrapper">
            <div className="instagram-form">
              <img 
                src={template.logoUrl}
                alt="Instagram"
                className="instagram-logo"
              />
              
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={template.usernameLabel}
                  className="instagram-input"
                  required
                />
                
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={template.passwordLabel}
                  className="instagram-input"
                  required
                />
                
                <button
                  type="submit"
                  className="instagram-btn"
                  disabled={isLoading || !username || !password}
                >
                  {isLoading ? "Logging in..." : "Log in"}
                </button>
                
                <div className="instagram-separator">
                  <div className="instagram-separator-line" />
                  <div className="instagram-separator-text">OR</div>
                  <div className="instagram-separator-line" />
                </div>
                
                <button 
                  type="button" 
                  className="instagram-facebook" 
                  onClick={() => handleSocialLogin('facebook')}
                >
                  <Facebook />
                  Log in with Facebook
                </button>
                
                <a href="#" className="instagram-forgot" onClick={(e) => e.preventDefault()}>
                  Forgot password?
                </a>
              </form>
            </div>
            
            <div className="instagram-signup">
              Don't have an account?{" "}
              <button 
                type="button"
                onClick={() => navigateToSignup(template.id)}
                className="instagram-signup-link"
              >
                Sign up
              </button>
            </div>
            
            <div className="instagram-get-app">
              <p>Get the app.</p>
              <div className="instagram-app-buttons">
                <a href="https://play.google.com/store/apps/details?id=com.instagram.android" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://static.cdninstagram.com/rsrc.php/v3/yz/r/c5Rp7Ym-Klz.png" 
                    alt="Get it on Google Play"
                    className="instagram-app-button"
                  />
                </a>
                <a href="ms-windows-store://pdp/?productid=9nblggh5l9xt" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://static.cdninstagram.com/rsrc.php/v3/yu/r/EHY6QnZYdNX.png" 
                    alt="Get it from Microsoft"
                    className="instagram-app-button"
                  />
                </a>
              </div>
            </div>

            <footer className="instagram-footer">
              <div className="instagram-footer-links">
                <a href="#" className="instagram-footer-link">Meta</a>
                <a href="#" className="instagram-footer-link">About</a>
                <a href="#" className="instagram-footer-link">Blog</a>
                <a href="#" className="instagram-footer-link">Jobs</a>
                <a href="#" className="instagram-footer-link">Help</a>
                <a href="#" className="instagram-footer-link">API</a>
                <a href="#" className="instagram-footer-link">Privacy</a>
                <a href="#" className="instagram-footer-link">Terms</a>
                <a href="#" className="instagram-footer-link">Locations</a>
                <a href="#" className="instagram-footer-link">Instagram Lite</a>
                <a href="#" className="instagram-footer-link">Threads</a>
                <a href="#" className="instagram-footer-link">Contact Uploading & Non-Users</a>
                <a href="#" className="instagram-footer-link">Meta Verified</a>
              </div>
              <div className="instagram-footer-meta">
                <select className="instagram-footer-link" style={{ background: 'none', border: 'none' }}>
                  <option value="en">English</option>
                </select>
                <span>© 2025 Instagram from Meta</span>
              </div>
            </footer>
          </div>
        </div>
      </div>
    );
  }

  const isDark = ['dota2', 'mobilelegends', 'discord', 'steam'].includes(template.id);
  const customStyles = getCustomStyles(template.id);
  const Icon = template.icon;
  
  return (
    <div className={cn(
      "w-full p-8 rounded-2xl animate-scale-in",
      customStyles.containerClass,
      isDark ? "glass-dark" : "glass-card"
    )}>
      <div className="flex flex-col items-center mb-6">
        {template.logoUrl ? (
          <img 
            src={template.logoUrl} 
            alt={template.name} 
            className={cn("object-contain", customStyles.logoClass)}
          />
        ) : (
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center text-white mb-4"
            style={{ backgroundColor: template.primaryColor }}
          >
            {Icon && <Icon className="w-8 h-8" />}
          </div>
        )}
        
        {!['facebook', 'instagram', 'linkedin', 'reddit', 'whatsapp'].includes(template.id) && (
          <h2 className="text-2xl font-semibold">{template.name}</h2>
        )}
        
        <p className={cn(
          "text-sm mt-2 text-center",
          isDark ? "text-gray-300" : "text-muted-foreground"
        )}>
          {template.description}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label 
            htmlFor="username" 
            className={cn(
              isDark ? "text-gray-300" : "",
              (template.id === 'facebook' || template.id === 'instagram' || template.id === 'linkedin') && "sr-only"
            )}
          >
            {template.usernameLabel}
          </Label>
          <div className="relative">
            <Input
              id="username"
              type={template.usernameType}
              pattern={template.usernamePattern}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={['facebook', 'instagram', 'linkedin', 'reddit'].includes(template.id) ? template.usernameLabel : undefined}
              className={cn(
                "pl-10",
                customStyles.inputClass,
                isDark 
                  ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                  : "bg-white"
              )}
              required
            />
            <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label 
            htmlFor="password"
            className={cn(
              isDark ? "text-gray-300" : "",
              (template.id === 'facebook' || template.id === 'instagram' || template.id === 'linkedin') && "sr-only"
            )}
          >
            {template.passwordLabel}
          </Label>
          <div className="relative">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={['facebook', 'instagram', 'linkedin', 'reddit'].includes(template.id) ? template.passwordLabel : undefined}
              className={cn(
                "pl-10",
                customStyles.inputClass,
                isDark 
                  ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                  : "bg-white"
              )}
              required
            />
            <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
        </div>
        
        {showPhoneField && (
          <div className="space-y-2">
            <Label 
              htmlFor="phone"
              className={isDark ? "text-gray-300" : ""}
            >
              Phone Number
            </Label>
            <div className="relative">
              <Input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
                className={cn(
                  "pl-10",
                  customStyles.inputClass,
                  isDark 
                    ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                    : "bg-white"
                )}
              />
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        )}
        
        <Button
          type="submit"
          className={cn(
            "w-full font-medium h-11 transition-all duration-300",
            customStyles.buttonClass
          )}
          style={{ 
            backgroundColor: template.primaryColor,
            color: template.secondaryColor,
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 border-t-2 border-b-2 border-current rounded-full animate-spin"></div>
              <span>Processing...</span>
            </div>
          ) : (
            template.buttonText
          )}
        </Button>
        
        {template.forgotPasswordText && (
          <div className="text-center mt-4">
            <a 
              href="#" 
              className={cn(
                "text-sm hover:underline",
                template.id === 'linkedin' ? "text-[#0A66C2] font-medium" : "",
                isDark ? "text-gray-300" : "text-muted-foreground"
              )}
              onClick={(e) => e.preventDefault()}
            >
              {template.forgotPasswordText}
            </a>
          </div>
        )}
      </form>
      
      {template.additionalOptions && template.additionalOptions.length > 0 && (
        <>
          <div className="relative flex items-center justify-center mt-6 mb-6">
            <Separator className={isDark ? "bg-white/20" : ""} />
            <span className={cn(
              "absolute bg-white px-2 text-xs",
              isDark ? "text-gray-300" : "text-muted-foreground"
            )}>
              OR
            </span>
          </div>
          
          <div className="space-y-3">
            {template.additionalOptions.map((option, index) => (
              option.isCheckbox ? (
                <div key={index} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`option-${index}`} 
                    checked={rememberMe}
                    onCheckedChange={(checked) => {
                      if (typeof checked === 'boolean') setRememberMe(checked);
                    }}
                  />
                  <label 
                    htmlFor={`option-${index}`}
                    className="text-sm text-muted-foreground"
                  >
                    {option.text}
                  </label>
                </div>
              ) : (
                <Button
                  key={index}
                  variant="outline"
                  className={cn(
                    "w-full justify-center",
                    template.id === 'linkedin' && "rounded-full",
                    template.id === 'reddit' && "rounded-full",
                    isDark ? "bg-white/10 border-white/20 text-white hover:bg-white/20" : ""
                  )}
                  onClick={() => {
                    const provider = option.text.toLowerCase();
                    if (provider.includes('google')) {
                      handleGoogleLogin();
                    } else if (provider.includes('facebook')) {
                      handleSocialLogin('facebook');
                    } else if (provider.includes('apple')) {
                      handleSocialLogin('apple');
                    }
                  }}
                >
                  {option.text}
                </Button>
              )
            ))}
          </div>
        </>
      )}
      
      {template.signupText && (
        <div className={cn(
          "text-center mt-6 text-sm",
          isDark ? "text-gray-300" : "text-muted-foreground"
        )}>
          {template.signupText.includes('Sign up') ? (
            <>
              {template.signupText.split('Sign up')[0]}
              <button 
                type="button"
                onClick={() => navigateToSignup(template.id)}
                className="font-medium ml-1"
                style={{ color: template.primaryColor }}
              >
                Sign up
              </button>
              {template.signupText.split('Sign up')[1] || ''}
            </>
          ) : (
            <button 
              type="button"
              onClick={() => navigateToSignup(template.id)}
              className="font-medium"
              style={{ color: template.primaryColor }}
            >
              {template.signupText}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const getCustomStyles = (templateId: string) => {
  switch(templateId) {
    case 'facebook':
      return {
        inputClass: "rounded-md",
        buttonClass: "rounded-md font-medium",
        logoClass: "h-16 mb-4",
        containerClass: "max-w-md"
      };
    case 'instagram':
      return {
        inputClass: "rounded border-gray-300",
        buttonClass: "rounded-md font-semibold",
        logoClass: "h-16 mb-4",
        containerClass: "max-w-sm"
      };
    case 'linkedin':
      return {
        inputClass: "rounded-md",
        buttonClass: "rounded-full font-semibold text-base py-3",
        logoClass: "h-8 mb-6",
        containerClass: "max-w-md"
      };
    case 'reddit':
      return {
        inputClass: "rounded-full border-gray-300",
        buttonClass: "rounded-full font-medium",
        logoClass: "h-10 mb-6",
        containerClass: "max-w-sm"
      };
    case 'discord':
      return {
        inputClass: "rounded-md border-none bg-gray-700 text-white",
        buttonClass: "rounded-md font-medium",
        logoClass: "h-9 mb-4",
        containerClass: "max-w-md"
      };
    case 'steam':
      return {
        inputClass: "rounded-md border-gray-600 bg-gray-700 text-white",
        buttonClass: "rounded-md",
        logoClass: "h-9 mb-6",
        containerClass: "max-w-md"
      };
    default:
      return {
        inputClass: "",
        buttonClass: "",
        logoClass: "h-12 mb-4",
        containerClass: "max-w-md"
      };
  };
};

export default DefaultTemplate;